import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@rook/shared/auth/AuthProvider";
import { Button } from "@rook/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@rook/shared/components/ui/card";
import { Checkbox } from "@rook/shared/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@rook/shared/components/ui/form";
import { Input } from "@rook/shared/components/ui/input";
import { usePageTitle } from "@rook/shared/lib/usePageTitle";


import { login, recordSignupAcceptances, register, updateAttributes } from "./api";
import {
  signupAttributesSchema,
  signupCredentialsSchema,
  type SignupAttributesInput,
  type SignupCredentialsInput,
} from "./schemas";
import { SIGNUP_USER_ACCEPTANCE_DOCS } from "../legal/legalDocuments";

/**
 * Two-step signup flow (matches the legacy contract):
 *
 *   Step 1 — credentials: email + password + confirm. POST
 *     /api/auth/register creates the Cognito user.
 *   Step 2 — attributes: first/last name + company. POST
 *     /api/auth/update-attributes fills the standard + custom
 *     Cognito attributes on the just-created user.
 *
 * After step 2 succeeds we auto-log-in (the user just typed both
 * pieces, no point making them re-enter at /).
 */
type CredentialsCache = SignupCredentialsInput | null;

const StepHeader = ({ step }: { step: 1 | 2 }) => (
  <CardHeader>
    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
      Create account · Step {step} of 2
    </p>
    <CardTitle className="text-2xl">
      {step === 1 ? "Get started" : "Tell us about your company"}
    </CardTitle>
    <CardDescription>
      {step === 1
        ? "Pick the email and password you'll use to sign in."
        : "These details show on invoices and inside the portal sidebar."}
    </CardDescription>
  </CardHeader>
);

const Step1Credentials = ({
  onSuccess,
}: {
  onSuccess: (credentials: SignupCredentialsInput) => void;
}) => {
  const form = useForm<SignupCredentialsInput>({
    resolver: zodResolver(signupCredentialsSchema),
    defaultValues: { email: "", password: "", passwordConfirm: "" },
  });

  const mutation = useMutation({
    mutationFn: register,
    onSuccess: (_data, variables) => onSuccess(variables),
  });

  const errorMessage =
    mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
      >
        {errorMessage && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  autoComplete="email"
                  placeholder="name@mail.com"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 chars, mix of upper, lower, digits"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="passwordConfirm"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm password</FormLabel>
              <FormControl>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder="Type the password again"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" size="lg" disabled={mutation.isPending}>
          {mutation.isPending ? "Creating…" : "Continue"}
        </Button>
      </form>
    </Form>
  );
};

const Step2Attributes = ({
  credentials,
}: {
  credentials: SignupCredentialsInput;
}) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const form = useForm<SignupAttributesInput>({
    resolver: zodResolver(signupAttributesSchema),
    defaultValues: {
      given_name: "",
      family_name: "",
      company: "",
      acceptedLegal: false,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: SignupAttributesInput) => {
      // `acceptedLegal` gates the form only — it's NOT a Cognito
      // attribute, so send just the profile fields to update-attributes.
      await updateAttributes(
        {
          given_name: data.given_name,
          family_name: data.family_name,
          company: data.company,
        },
        { email: credentials.email, password: credentials.password },
      );
      // Auto-login. The user just confirmed both pieces; no
      // value in making them re-enter on /.
      await login({
        email: credentials.email,
        password: credentials.password,
        remember: true,
      });
      // Session now exists → record the clickwrap acceptances in the
      // audit trail (cpf-acceptances). Best-effort: never blocks the
      // dashboard nav (the enforced checkbox is the legal act).
      await recordSignupAcceptances();
    },
    onSuccess: async () => {
      // GTM sign_up event (the legacy fired this on the dashboard
      // transition; the SPA fires it here, after both steps + login
      // succeed end-to-end).
      const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
      w.dataLayer = w.dataLayer || [];
      w.dataLayer.push({ event: "sign_up", form_name: "sign_up" });
      await auth.refresh();
      navigate("/home", { replace: true });
    },
  });

  const errorMessage =
    mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
      >
        {errorMessage && (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {errorMessage}
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="given_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First name</FormLabel>
                <FormControl>
                  <Input autoComplete="given-name" placeholder="Ada" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="family_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last name</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="family-name"
                    placeholder="Lovelace"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="company"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company</FormLabel>
              <FormControl>
                <Input
                  autoComplete="organization"
                  placeholder="Acme Inc."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="acceptedLegal"
          render={({ field }) => (
            <FormItem className="flex items-start gap-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-label="Accept the legal agreements"
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal leading-snug">
                  I agree to ROOK&apos;s{" "}
                  {SIGNUP_USER_ACCEPTANCE_DOCS.map((doc, i) => (
                    <span key={doc.slug}>
                      {i > 0 &&
                        (i === SIGNUP_USER_ACCEPTANCE_DOCS.length - 1
                          ? ", and "
                          : ", ")}
                      <Link
                        to={`/legal/${doc.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-rook-light hover:underline"
                      >
                        {doc.title}
                      </Link>
                    </span>
                  ))}
                  .
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <Button type="submit" size="lg" disabled={mutation.isPending}>
          {mutation.isPending ? "Finishing…" : "Create account"}
        </Button>
      </form>
    </Form>
  );
};

export const SignupView = () => {
  usePageTitle("Create account");
  const [credentials, setCredentials] = useState<CredentialsCache>(null);
  const step: 1 | 2 = credentials ? 2 : 1;

  return (
    <Card>
      <StepHeader step={step} />
      <CardContent>
        {credentials === null ? (
          <Step1Credentials onSuccess={setCredentials} />
        ) : (
          <Step2Attributes credentials={credentials} />
        )}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/" className="font-semibold text-rook-light hover:underline">
            Sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
};
